/**
 * Page-view tracking — dual sink:
 *   1. Google Analytics 4 (via gtag, loaded in index.html)
 *   2. Invilic backend  (POST /api/track/pageview — self-hosted analytics)
 *
 * The backend call is fire-and-forget; errors are silently swallowed
 * so tracking never blocks navigation or causes visible errors.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Only send to backend for public/SEO pages — mirrors the server-side whitelist
const TRACKED_PATHS = new Set([
  '/',
  '/free-invoice-generator',
  '/gst-invoice-generator',
  '/freelancer-invoice-generator',
  '/vat-invoice-generator',
  '/invoice-generator-india',
  '/international-invoice-generator',
  '/coach-invoice-generator',
  '/gst-invoice-template',
  '/freelancer-invoice-template',
  '/invoice-template-india',
  '/service-invoice-template',
  '/coach-invoice-template',
  '/vat-invoice-template',
  '/self-employed-invoice-template',
  '/contractor-invoice-template',
  '/invoice-generator-uk',
  '/invoice-generator-australia',
  '/invoice-generator-us',
  '/invoice-generator-canada',
  '/invoice-generator-uae',
  '/invoice-generator-singapore',
]);

function trackGa(path) {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', { page_path: path });
}

function trackBackend(path) {
  const cleanPath = path.split('?')[0].replace(/\/$/, '') || '/';
  if (!TRACKED_PATHS.has(cleanPath)) return;
  fetch(`${API_URL}/api/track/pageview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: cleanPath }),
  }).catch(() => {}); // fire-and-forget
}

export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname + location.search;
    trackGa(path);
    trackBackend(location.pathname);
  }, [location]);
}

export { trackGa as trackPageView };
